import React, { useEffect, useState, useMemo } from 'react';
import { Box, Button, ActionMenu, ActionList, IconButton, useConfirm, Select } from '@primer/react';
import { PlusIcon, SyncIcon, PencilIcon, TrashIcon, KebabHorizontalIcon, DownloadIcon } from '@primer/octicons-react';

import { useCommonContext } from '../../../contexts/common';
import { useCommonSelectedHook } from '../../../hooks/useCommonSelectedHook';
import { KhoThuocTonKhoItemResponse } from '../../../models/response/category/KhoThuocTonKhoItemResponse';
import { KhoThuocLichSuItemResponse } from '../../../models/response/category/KhoThuocLichSuItemResponse';
import { khoThuocApi } from '../../../api/khoThuocApi';
import { NotifyHelper } from '../../../helpers/toast';
import DataTable from '../../../components/ui/data-table';
import KhoThuocNhapModal from './KhoThuocNhapModal';
import KhoThuocEditModal from './KhoThuocEditModal';

const KhoThuocPage: React.FC = () => {
    const { translate } = useCommonContext();
    const { dm_coso_id } = useCommonSelectedHook();

    // State
    const [tonKhoData, setTonKhoData] = useState<KhoThuocTonKhoItemResponse[]>([]);
    const [lichSuData, setLichSuData] = useState<KhoThuocLichSuItemResponse[]>([]);
    const [isLoadingTonKho, setIsLoadingTonKho] = useState(false);
    const [isLoadingLichSu, setIsLoadingLichSu] = useState(false);
    const [selectedLoaiThuocId, setSelectedLoaiThuocId] = useState<number | null>(null);
    const [isShowNhapModal, setIsShowNhapModal] = useState(false);
    const [isShowEditModal, setIsShowEditModal] = useState(false);
    const [editingData, setEditingData] = useState<KhoThuocLichSuItemResponse | undefined>(undefined);
    const [filterLoaiGiaoDich, setFilterLoaiGiaoDich] = useState<string>(""); // "" = Tất cả, "NHAP" = Nhập kho, "XUAT" = Xuất kho
    const [isExporting, setIsExporting] = useState(false);
    const confirm = useConfirm();

    // Filter dữ liệu lịch sử theo loại giao dịch
    const filteredLichSuData = useMemo(() => {
        if (!filterLoaiGiaoDich) {
            return lichSuData; // Hiển thị tất cả
        }
        return lichSuData.filter(item => item.loai_giao_dich === filterLoaiGiaoDich);
    }, [lichSuData, filterLoaiGiaoDich]);

    // Load data
    useEffect(() => {
        loadTonKhoData();
        loadAllLichSuData();
    }, [dm_coso_id]);

    const loadTonKhoData = async () => {
        try {
            setIsLoadingTonKho(true);
            const response = await khoThuocApi.selectTonKho();
            if (response.is_success) {
                // Lọc theo dm_coso_id
                const filteredData = (response.data || []).filter((item: KhoThuocTonKhoItemResponse) => item.dm_coso_id === dm_coso_id);
                setTonKhoData(filteredData);
            } else {
                NotifyHelper.Error(response.message || "Lỗi khi tải dữ liệu tồn kho");
            }
        } catch (error) {
            console.error('Error loading ton kho:', error);
            NotifyHelper.Error("Lỗi khi tải dữ liệu tồn kho");
        } finally {
            setIsLoadingTonKho(false);
        }
    };

    const loadAllLichSuData = async () => {
        try {
            setIsLoadingLichSu(true);
            const response = await khoThuocApi.selectAllLichSu();
            if (response.is_success) {
                // Lọc theo dm_coso_id
                const filteredData = (response.data || []).filter((item: KhoThuocLichSuItemResponse) => item.dm_coso_id === dm_coso_id);
                setLichSuData(filteredData);
            } else {
                NotifyHelper.Error(response.message || "Lỗi khi tải dữ liệu lịch sử");
            }
        } catch (error) {
            console.error('Error loading lich su:', error);
            NotifyHelper.Error("Lỗi khi tải dữ liệu lịch sử");
        } finally {
            setIsLoadingLichSu(false);
        }
    };

    const loadLichSuByLoaiThuoc = async (loaiThuocId: number) => {
        try {
            setIsLoadingLichSu(true);
            const response = await khoThuocApi.selectLichSuByLoaiThuoc(loaiThuocId);
            if (response.is_success) {
                setLichSuData(response.data || []);
            } else {
                NotifyHelper.Error(response.message || "Lỗi khi tải dữ liệu lịch sử");
            }
        } catch (error) {
            console.error('Error loading lich su by loai thuoc:', error);
            NotifyHelper.Error("Lỗi khi tải dữ liệu lịch sử");
        } finally {
            setIsLoadingLichSu(false);
        }
    };

    const handleSelectTonKho = (data: KhoThuocTonKhoItemResponse) => {
        // Nếu click vào dòng đã chọn thì bỏ chọn
        if (selectedLoaiThuocId === data.ts_hocsinh_lichsu_yte_loaithuoc_id) {
            setSelectedLoaiThuocId(null);
            loadAllLichSuData(); // Load tất cả lịch sử
        } else {
            setSelectedLoaiThuocId(data.ts_hocsinh_lichsu_yte_loaithuoc_id);
            loadLichSuByLoaiThuoc(data.ts_hocsinh_lichsu_yte_loaithuoc_id);
        }
    };

    const handleRefresh = () => {
        loadTonKhoData();
        if (selectedLoaiThuocId) {
            loadLichSuByLoaiThuoc(selectedLoaiThuocId);
        } else {
            loadAllLichSuData();
        }
    };

    const handleNhapSuccess = () => {
        handleRefresh();
    };

    const handleEdit = (data: KhoThuocLichSuItemResponse) => {
        setEditingData(data);
        setIsShowEditModal(true);
    };

    const handleDelete = async (data: KhoThuocLichSuItemResponse) => {
        if (await confirm({
            title: 'Xác nhận xóa',
            content: `Bạn có chắc chắn muốn xóa giao dịch này không?`,
            confirmButtonContent: 'Xóa',
            cancelButtonContent: 'Hủy'
        })) {
            try {
                const response = await khoThuocApi.delete(data.id);
                if (response.is_success) {
                    NotifyHelper.Success("Xóa thành công");
                    handleRefresh();
                } else {
                    NotifyHelper.Error(response.message || "Xóa thất bại");
                }
            } catch (error) {
                console.error('Error deleting:', error);
                NotifyHelper.Error("Xóa thất bại");
            }
        }
    };

    const handleEditSuccess = () => {
        handleRefresh();
        setIsShowEditModal(false);
        setEditingData(undefined);
    };

    const handleExport = async () => {
        try {
            setIsExporting(true);

            // Lấy năm học hiện tại (có thể cần điều chỉnh logic này)
            const currentYear = new Date().getFullYear();
            const nam_hoc = `${currentYear}-${currentYear + 1}`;

            const response = await khoThuocApi.exportToTemplate({
                nam_hoc: nam_hoc,
                dm_coso_id: dm_coso_id
            });

            if (response.is_success && response.data) {
                // Mở link download trong tab mới
                window.open(response.data, '_blank');
                NotifyHelper.Success("Export thành công");
            } else {
                NotifyHelper.Error(response.message || "Export thất bại");
            }
        } catch (error) {
            console.error('Error exporting:', error);
            NotifyHelper.Error("Export thất bại");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box sx={{ p: 3, height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="primary"
                        leadingVisual={PlusIcon}
                        onClick={() => setIsShowNhapModal(true)}
                    >
                        Nhập thuốc
                    </Button>
                    <Button
                        leadingVisual={SyncIcon}
                        onClick={handleRefresh}
                    >
                        Làm mới
                    </Button>
                    <Button
                        leadingVisual={DownloadIcon}
                        onClick={handleExport}
                        disabled={isExporting}
                    >
                        {isExporting ? 'Đang xuất...' : 'Export Excel'}
                    </Button>
                </Box>
            </Box>

            {/* Main Content */}
            <Box sx={{ display: 'flex', gap: 3, flex: 1, overflow: 'hidden' }}>
                {/* Left Panel - Tồn kho */}
                <Box sx={{ width: '45%', display: 'flex', flexDirection: 'column' }}>
                    <DataTable
                        height="calc(100vh - 290px)"
                        data={tonKhoData}
                        isLoading={isLoadingTonKho}
                        title="Tồn kho thuốc"
                        subTitle={`${translate("Base.Label.TotalCount")}: ${tonKhoData.length}`}
                        searchEnable
                        paging={{ enable: true, pageSizeItems: [20, 50, 100] }}
                        columns={[
                            {
                                id: "ten_thuoc",
                                caption: "Tên thuốc",
                                dataField: "ten_thuoc",
                                width: "200px",
                                cellRender: (data: KhoThuocTonKhoItemResponse) => (
                                    <Box
                                        sx={{
                                            cursor: 'pointer',
                                            padding: '8px',
                                            margin: '-8px',
                                            borderRadius: '4px',
                                            backgroundColor: selectedLoaiThuocId === data.ts_hocsinh_lichsu_yte_loaithuoc_id ? '#e6f3ff' : 'transparent',
                                            fontWeight: selectedLoaiThuocId === data.ts_hocsinh_lichsu_yte_loaithuoc_id ? 'bold' : 'normal',
                                            '&:hover': {
                                                backgroundColor: selectedLoaiThuocId === data.ts_hocsinh_lichsu_yte_loaithuoc_id ? '#e6f3ff' : '#f6f8fa'
                                            }
                                        }}
                                        onClick={() => handleSelectTonKho(data)}
                                    >
                                        {data.ten_thuoc}
                                    </Box>
                                ),
                            },
                            {
                                id: "ten_donvitinh",
                                caption: "Đơn vị tính",
                                dataField: "ten_donvitinh",
                                width: "100px",
                            },
                            {
                                id: "ton_kho",
                                caption: "Tồn kho",
                                dataField: "ton_kho",
                                width: "100px",
                                align: "right",
                                cellRender: (data: KhoThuocTonKhoItemResponse) => (
                                    <Box sx={{
                                        fontWeight: 'bold',
                                        color: data.ton_kho <= 0 ? 'danger.fg' : 'success.fg'
                                    }}>
                                        {data.ton_kho}
                                    </Box>
                                ),
                            },
                        ]}
                    />
                </Box>

                {/* Right Panel - Lịch sử */}
                <Box sx={{ width: '55%', display: 'flex', flexDirection: 'column' }}>
                    <DataTable
                        height="calc(100vh - 290px)"
                        data={filteredLichSuData}
                        isLoading={isLoadingLichSu}
                        title={ "Lịch sử nhập xuất thuốc" }
                        subTitle={`${translate("Base.Label.TotalCount")}: ${filteredLichSuData.length}`}
                        searchEnable
                        paging={{ enable: true, pageSizeItems: [20, 50, 100] }}
                        // actionComponent={
                            
                        //         // <Box sx={{ minWidth: '150px' }}>
                        //         //     <Select
                        //         //         value={filterLoaiGiaoDich}
                        //         //         onChange={(e) => setFilterLoaiGiaoDich(e.target.value)}
                        //         //         size="small"
                        //         //     >
                        //         //         <Select.Option value="">Tất cả</Select.Option>
                        //         //         <Select.Option value="NHAP">Nhập kho</Select.Option>
                        //         //         <Select.Option value="XUAT">Xuất kho</Select.Option>
                        //         //     </Select>
                        //         // </Box>
                        // }
                        columns={[
                            {
                                id: "created_time",
                                caption: "Ngày giao dịch",
                                dataField: "created_time",
                                width: "120px",
                                cellRender: (data: KhoThuocLichSuItemResponse) => (
                                    <span>{new Date(data.created_time || '').toLocaleDateString('vi-VN')}</span>
                                ),
                            },
                            {
                                id: "ten_thuoc",
                                caption: "Tên thuốc",
                                dataField: "ten_thuoc",
                                width: "150px",
                            },
                            {
                                id: "loai_giao_dich",
                                caption: "Loại",
                                dataField: "loai_giao_dich",
                                width: "100px",
                                cellRender: (data: KhoThuocLichSuItemResponse) => (
                                    <Box sx={{ 
                                        px: 2, 
                                        py: 1, 
                                        borderRadius: 2,
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                        textAlign: 'center',
                                        backgroundColor: data.loai_giao_dich === 'NHAP' ? 'success.subtle' : 'danger.subtle',
                                        color: data.loai_giao_dich === 'NHAP' ? 'success.fg' : 'danger.fg'
                                    }}>
                                        {data.loai_giao_dich === 'NHAP' ? 'Nhập kho' : 'Xuất kho'}
                                    </Box>
                                ),
                            },
                            {
                                id: "so_luong",
                                caption: "Số lượng",
                                dataField: "so_luong",
                                width: "80px",
                                align: "right",
                            },
                            {
                                id: "hsd",
                                caption: "HSD",
                                dataField: "hsd",
                                width: "80px",
                            },
                            {
                                id: "ghi_chu",
                                caption: "Ghi chú",
                                dataField: "ghi_chu",
                                width: "150px",
                                cellRender: (data: KhoThuocLichSuItemResponse) => (
                                    <span>
                                        {data.loai_giao_dich === "XUAT" ? "Xuất thuốc y tế" : data.ghi_chu}
                                    </span>
                                ),
                            },
                            {
                                id: "actions",
                                align: "center",
                                width: "60px",
                                cellRender: (data: KhoThuocLichSuItemResponse) => (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {data.loai_giao_dich === "XUAT" ? (
                                            // Disable actions cho bản ghi XUAT
                                            <IconButton
                                                icon={KebabHorizontalIcon}
                                                variant="invisible"
                                                size="small"
                                                aria-label="Không thể thao tác"
                                                disabled
                                                sx={{ opacity: 0.3 }}
                                            />
                                        ) : (
                                            // Enable actions cho bản ghi NHAP
                                            <ActionMenu>
                                                <ActionMenu.Anchor>
                                                    <IconButton
                                                        icon={KebabHorizontalIcon}
                                                        variant="invisible"
                                                        size="small"
                                                        aria-label="Thao tác"
                                                    />
                                                </ActionMenu.Anchor>
                                                <ActionMenu.Overlay>
                                                    <ActionList>
                                                        <ActionList.Item onSelect={() => handleEdit(data)}>
                                                            <ActionList.LeadingVisual>
                                                                <PencilIcon />
                                                            </ActionList.LeadingVisual>
                                                            Sửa
                                                        </ActionList.Item>
                                                        <ActionList.Item
                                                            variant="danger"
                                                            onSelect={() => handleDelete(data)}
                                                        >
                                                            <ActionList.LeadingVisual>
                                                                <TrashIcon />
                                                            </ActionList.LeadingVisual>
                                                            Xóa
                                                        </ActionList.Item>
                                                    </ActionList>
                                                </ActionMenu.Overlay>
                                            </ActionMenu>
                                        )}
                                    </Box>
                                ),
                            }
                        ]}
                    />
                </Box>
            </Box>

            {/* Modal nhập thuốc */}
            <KhoThuocNhapModal
                isOpen={isShowNhapModal}
                onClose={() => setIsShowNhapModal(false)}
                onSaveSuccess={handleNhapSuccess}
            />

            {/* Modal sửa giao dịch */}
            <KhoThuocEditModal
                isOpen={isShowEditModal}
                onClose={() => {
                    setIsShowEditModal(false);
                    setEditingData(undefined);
                }}
                onSaveSuccess={handleEditSuccess}
                editingData={editingData}
            />
        </Box>
    );
};

export default KhoThuocPage;
